import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../AppConfig";
import { PageRoutes } from "../constants/routes";
import { ErrorMessageManager } from "../core/http/ErrorMessageManager";
import { LocalStorageManager } from "../core/storage/LocalStorageManager";
import { appActions } from "../ducks/app";
import {
  couponActions,
  couponSelectors,
  MenuExternalCoupon,
} from "../ducks/coupon";
import { menuActions, menuSelectors } from "../ducks/menu";
import { getDatePlusMinutes, isDateTimeBefore } from "../utils/date";
import { useNavigation } from "./useNavigation";

export type CouponHook = ReturnType<typeof useCoupon>;
export type RedirectPickupInfo = {
  storeId: number;
  pickupDate: string;
  storeShortestTimeUntilPickup: number;
  redirectUrl: string;
  returnAfterLogin?: boolean;
};

export const useCoupon = () => {
  const dispatch = useDispatch();
  const { navigateToTopPage, navigateToPage } = useNavigation();

  const coupons = useSelector(couponSelectors.coupons);
  const appliedCoupons = useSelector(couponSelectors.appliedCoupons);
  const selectedAppCoupons = useSelector(couponSelectors.selectedAppCoupons);

  const menusInCart = useSelector(menuSelectors.menusInCart);

  useEffect(() => {
    if (!coupons) dispatch(couponActions.getCoupons.started({}));

    if (!appliedCoupons) dispatch(couponActions.getAppliedCoupons.started({}));
  }, []);

  const getCouponName = useCallback(
    (code: string) => {
      if (!coupons) return "";

      const coupon = coupons.find((coupon) => coupon.code === code);
      return coupon?.name ?? "";
    },
    [coupons],
  );

  const normalCouponCodes = useMemo(() => {
    if (!appliedCoupons) return [];

    return appliedCoupons
      .filter((coupon) => !coupon.isAppCoupon)
      .map((coupon) => coupon.code);
  }, [appliedCoupons]);

  const totalCouponCodes = useMemo(() => {
    if (!appliedCoupons) return [];

    return appliedCoupons
      .filter((coupon) => coupon.isTotalCoupon)
      .map((coupon) => coupon.code);
  }, [appliedCoupons]);

  const appCouponCodes = useMemo(() => {
    const selectedAppCouponCodes =
      selectedAppCoupons?.map((coupon) => coupon.code) ?? [];
    if (!appliedCoupons) return selectedAppCouponCodes;

    return selectedAppCoupons
      ? [
          ...selectedAppCouponCodes,
          ...appliedCoupons
            .filter((coupon) => coupon.isAppCoupon)
            .filter((coupon) => !selectedAppCouponCodes.includes(coupon.code))
            .map((coupon) => coupon.code),
        ]
      : appliedCoupons
          .filter((coupon) => coupon.isAppCoupon)
          .map((coupon) => coupon.code);
  }, [selectedAppCoupons, appliedCoupons]);

  const onCouponError = useCallback(
    (
      errorCode: string,
      couponCodes: string[],
      isAppCoupon?: boolean,
      showCouponName?: boolean,
    ) => {
      dispatch(
        appActions.showModal.started({
          title: ErrorMessageManager.Instance.getErrorMessage(errorCode),
          subTitle: couponCodes
            .map(
              (couponCode) =>
                `「${showCouponName ? getCouponName(couponCode) : couponCode}」`,
            )
            .join(`\n`),
          nextButtonLabel: "閉じる",
          handleNext: () => {
            dispatch(appActions.closeModal());

            if (isAppCoupon) {
              dispatch(
                couponActions.removeSelectedAppCoupon.started({
                  couponCodes,
                }),
              );
            }
          },
        }),
      );
    },
    [coupons],
  );

  const applyCoupon = useCallback(
    (
      storeId: number,
      pickupDate: string,
      storeShortestTimeUntilPickup: number,
      couponCodes: string[],
      isAppCoupon?: boolean,
      menuExternalCoupons?: MenuExternalCoupon[],
      onApplied?: () => void,
    ) => {
      if (couponCodes.length === 0) return;
      dispatch(appActions.showLoading());
      dispatch(
        couponActions.applyCoupon.started({
          store_id: storeId,
          pickup_date: pickupDate,
          coupon_code: couponCodes,
          isAppCoupon,
          menuExternalCoupons,
          onError: (error) => {
            dispatch(appActions.hideLoading());

            const errorCouponCodes =
              error?.response?.data.data?.additional ?? [];
            if (errorCouponCodes.length > 0) {
              onCouponError(
                error?.response?.data.data?.code ?? "9900-9999",
                errorCouponCodes,
                isAppCoupon,
              );
            }
          },
          onSuccess: ({ couponMenus, appliedCoupons }) => {
            const isReservation = isDateTimeBefore(
              getDatePlusMinutes(new Date(), storeShortestTimeUntilPickup + 5),
              pickupDate,
            );
            const totalCoupons =
              appliedCoupons
                .filter((coupon) => coupon.isTotalCoupon)
                .map((coupon) => coupon.code) ?? [];
            dispatch(
              couponActions.checkAppliedCouponMenu.started({
                couponMenus,
                appliedCoupons,
                isAppCoupon,
                store_id: storeId,
                pickup_date: pickupDate,
                total_coupons: totalCoupons,
                menus:
                  menusInCart && menusInCart.length > 0
                    ? menusInCart.map((menu) => ({
                        id: menu.menuId,
                        quantity: menu.amount,
                        option: menu.optionId ? [menu.optionId] : [],
                        couponCode: menu.couponCode,
                      }))
                    : couponMenus.map((menu) => ({
                        id: menu.menuId,
                        quantity: menu.amount,
                        option: menu.optionId ? [menu.optionId] : [],
                        couponCode: menu.couponCode,
                      })),
                is_reservation: isReservation,
                memo: "",
                onComplete: () => dispatch(appActions.hideLoading()),
                onError: (error) => {
                  const errorCouponCodes =
                    error?.response?.data.data?.additional ?? [];
                  if (errorCouponCodes.length > 0) {
                    onCouponError(
                      error?.response?.data.data?.code ?? "9900-9999",
                      errorCouponCodes,
                      isAppCoupon,
                      true,
                    );
                  }
                },
                onSuccess: (menus) => {
                  dispatch(menuActions.addCouponMenusToCart.started({ menus }));
                  onApplied?.();
                },
              }),
            );
          },
        }),
      );
    },
    [appliedCoupons, menusInCart, onCouponError],
  );

  const showRemoveCouponModal = useCallback(
    (errorCouponCodes: string[], onClose: () => void) => {
      dispatch(
        appActions.showModal.started({
          title: `ご指定の店舗では\n現在ご利用になれないクーポンです`,
          subTitle: `※アプリ連携クーポンを削除すると当該クーポンは利用できなくなります\n${errorCouponCodes.map((couponCode) => getCouponName(couponCode)).join("\n")}`,
          nextButtonLabel: "店舗選択に戻る",
          handleNext: () => {
            navigateToPage(PageRoutes.HOME, false, { isRegular: true });
            dispatch(appActions.closeModal());
          },
          closeButtonLabel: "削除する",
          handleClose: () => {
            errorCouponCodes.forEach((couponCode) => {
              if (
                selectedAppCoupons &&
                selectedAppCoupons.some((coupon) => coupon.code === couponCode)
              ) {
                dispatch(
                  couponActions.removeSelectedAppCoupon.started({
                    couponCodes: [couponCode],
                  }),
                );
              }

              const couponToRemove = appliedCoupons
                ?.filter((coupon) => coupon.code === couponCode)
                .map((coupon) => coupon.uuid);
              if (couponToRemove) {
                dispatch(
                  couponActions.removeAppliedCoupon.started({
                    couponUuids: couponToRemove,
                  }),
                );

                dispatch(
                  menuActions.removeMenuFromCartByCouponUuids.started({
                    couponUuids: couponToRemove,
                  }),
                );
              }
            });

            onClose();
            dispatch(appActions.closeModal());
          },
        }),
      );
    },
    [selectedAppCoupons, appliedCoupons],
  );

  const validateCoupon = useCallback(
    (
      storeId: number,
      pickupDate: string,
      storeShortestTimeUntilPickup: number,
      onSuccess: (success: string[]) => void,
      validateOnly: boolean = false,
    ) => {
      dispatch(
        couponActions.validateCoupon.started({
          store_id: storeId,
          pickup_date: pickupDate,
          coupon_code: normalCouponCodes,
          extra_coupon: appCouponCodes,
          onSuccess: ({ success, errors }) => {
            if (errors.length === 0) {
              onSuccess?.(success);
            } else {
              showRemoveCouponModal(errors, () => {
                if (validateOnly) {
                  onSuccess?.(success);
                  return;
                }
                if (success.length > 0) {
                  applyCoupon(
                    storeId,
                    pickupDate,
                    storeShortestTimeUntilPickup,
                    success,
                    true,
                  );
                }
              });
            }
          },
        }),
      );
    },
    [normalCouponCodes, appCouponCodes],
  );

  const handleRemoveCoupon = useCallback(
    (couponUuid: string) => {
      dispatch(
        appActions.showModal.started({
          title: `クーポンを削除します。\nよろしいですか？`,
          nextButtonLabel: "はい",
          closeButtonLabel: "閉じる",
          handleNext: () => {
            const couponToRemove = appliedCoupons?.find(
              (coupon) => coupon.uuid === couponUuid,
            );
            if (couponToRemove) {
              dispatch(
                couponActions.removeAppliedCoupon.started({
                  couponUuids: [couponToRemove.uuid],
                }),
              );
              dispatch(
                menuActions.removeMenuFromCartByCouponUuids.started({
                  couponUuids: [couponToRemove.uuid],
                }),
              );
            }
            dispatch(appActions.closeModal());
          },
          handleClose: () => dispatch(appActions.closeModal()),
        }),
      );
    },
    [appliedCoupons, menusInCart],
  );

  const saveRedirectInfo = useCallback(
    ({
      storeId,
      pickupDate,
      storeShortestTimeUntilPickup,
      redirectUrl,
      returnAfterLogin,
    }: {
      storeId: number;
      pickupDate: string;
      storeShortestTimeUntilPickup: number;
      redirectUrl: string;
      returnAfterLogin?: boolean;
    }) => {
      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["pickupInfo"],
        {
          storeId,
          pickupDate,
          storeShortestTimeUntilPickup,
          redirectUrl,
          returnAfterLogin: returnAfterLogin ?? false,
        },
      );
    },
    [],
  );

  const handleInvalidCouponAppToken = useCallback(
    (info: RedirectPickupInfo) => {
      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey["appToken"],
      );
      saveRedirectInfo({
        ...info,
        returnAfterLogin: true,
      });
      navigateToPage(PageRoutes.APP_LINKAGE, true);
    },
    [saveRedirectInfo],
  );

  const handleUseAppCoupon = useCallback(
    (info: RedirectPickupInfo) => {
      const appToken = LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["appToken"],
      );

      saveRedirectInfo(info);

      if (!!appToken) navigateToPage(PageRoutes.APP_COUPON_LIST, false);
      else navigateToPage(PageRoutes.APP_COUPON, false);
    },
    [navigateToPage, saveRedirectInfo],
  );

  const handleErrorAppFreeCouponMaxLimit = useCallback(
    (onClose?: () => void) => {
      dispatch(
        appActions.showModal.started({
          title: "無料クーポンは最大3枚までご利用いただけます。",
          warningText:
            "※クーポンはそれぞれ6時間ごとに使用履歴がリセットされます。",
          nextButtonLabel: "閉じる",
          handleNext: () => {
            onClose?.();
            dispatch(appActions.closeModal());
          },
        }),
      );
    },
    [],
  );

  return {
    coupons,
    appliedCoupons,
    selectedAppCoupons,
    normalCouponCodes,
    totalCouponCodes,
    appCouponCodes,
    applyCoupon,
    validateCoupon,
    getCouponName,
    handleRemoveCoupon,
    handleUseAppCoupon,
    handleInvalidCouponAppToken,
    handleErrorAppFreeCouponMaxLimit,
  };
};
